import { BUILD_HASH, APP_VERSION, APP_ENV } from "@/config/version";

export interface ErrorReport {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  buildHash: string;
  appVersion: string;
  environment: string;
  extra?: Record<string, unknown>;
}

const ERROR_LOG_KEY = "wordpop-error-log";
const MAX_ERROR_LOG = 20;

// Generate a short unique ID for errors
function generateErrorId(): string {
  return `err_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

// Get stored error log
export function getErrorLog(): ErrorReport[] {
  try {
    const stored = localStorage.getItem(ERROR_LOG_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save error to localStorage
function saveErrorLog(errors: ErrorReport[]): void {
  try {
    localStorage.setItem(ERROR_LOG_KEY, JSON.stringify(errors));
  } catch {
    // Storage full or unavailable
  }
}

// Log an error to localStorage
export function logError(
  error: Error,
  componentStack?: string,
  extra?: Record<string, unknown>
): ErrorReport {
  const report: ErrorReport = {
    id: generateErrorId(),
    timestamp: new Date().toISOString(),
    message: error.message,
    stack: error.stack,
    componentStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    buildHash: BUILD_HASH,
    appVersion: APP_VERSION,
    environment: APP_ENV,
    extra,
  };

  // Add to log
  const log = getErrorLog();
  log.unshift(report);
  
  // Keep only recent errors
  if (log.length > MAX_ERROR_LOG) {
    log.splice(MAX_ERROR_LOG);
  }
  
  saveErrorLog(log);

  // Console log in development
  if (APP_ENV === "development") {
    console.group("🚨 Error Logged");
    console.error("Error:", error);
    console.log("Report ID:", report.id);
    console.log("Component Stack:", componentStack);
    console.groupEnd();
  }

  return report;
}

// Clear error log
export function clearErrorLog(): void {
  try {
    localStorage.removeItem(ERROR_LOG_KEY);
  } catch {
    // Ignore
  }
}

// Get error summary for sharing
export function getErrorSummary(): string {
  const log = getErrorLog();
  if (log.length === 0) return "No errors logged.";

  const latest = log[0];
  return `Error ID: ${latest.id}\nTime: ${new Date(latest.timestamp).toLocaleString()}\nMessage: ${latest.message}\nBuild: ${latest.buildHash}\nURL: ${latest.url}\nTotal Errors: ${log.length}`;
}

// Format error report for copying
export function formatErrorReport(report: ErrorReport): string {
  return `## Error Report\n**ID:** ${report.id}\n**Time:** ${report.timestamp}\n**Build:** v${report.appVersion}+${report.buildHash}\n**Environment:** ${report.environment}\n**URL:** ${report.url}\n\n### Error Message\n\`\`\`\n${report.message}\n\`\`\`\n\n### Stack Trace\n\`\`\`\n${report.stack || "No stack trace available"}\n\`\`\`\n\n${report.componentStack ? `### Component Stack\n\`\`\`\n${report.componentStack}\n\`\`\`` : ""}\n\n### User Agent\n${report.userAgent}\n`;
}

// Global error handler for uncaught errors
export function setupGlobalErrorHandler(): void {
  // Handle uncaught errors
  window.addEventListener("error", (event) => {
    logError(
      event.error || new Error(event.message),
      undefined,
      { type: "uncaught", filename: event.filename, lineno: event.lineno, colno: event.colno }
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    logError(error, undefined, { type: "unhandledrejection" });
  });
}
